import { useState, useEffect, useMemo, FC } from 'react'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { Format } from 'services/Dates'
import { hasAccessToThis } from 'services/Utilities'
import { TrackAction } from 'services/TrackAction'
import { closeModal } from 'store/actions/modals'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Form, Checkbox } from 'components/Forms'
import { EventSummaryList, UserInput } from 'components/Schedule'
import { Loading } from 'components/UI'
import useApi from 'components/Hooks/useApi'
import { Alert, Input, Card } from 'ui'

import { t } from 'translations'

type Props = {
  _id?: string
  ids?: string[]
}

type ValuesType = {
  employeeId: string
  sendCancellationEmails: boolean
  sendNotificationForNewEmployee: boolean
  updateFutureRecurrence: boolean
}

type MultipleValuesInputType = {
  children: React.ReactNode
  className?: string
  name?: string
  values: ValuesType
  setFieldValue: (field: string, value: string | null) => void
  module?: string
  disabled?: boolean
  parseEvents: (data: MData.Event[]) => MData.Event[]
  selectedEvents: MData.Event[]
}

const ScheduleAssignEmployeeModal: FC<Props> = ({ _id, ids }) => {
  const shiftIds = useMemo(() => (_id ? [_id] : ids), [_id, ids]) as string[]
  const dispatch = useDispatch()
  const [errorLoading, setErrorLoading] = useState(false)

  // Queries definition
  const {
    isLoading,
    response: selectedEvents,
    handleApi: fetchselectedEvents
  } = useApi<MData.Event[]>({
    method: 'POST',
    url: 'schedule/schedule/query',
    initialLoad: !!shiftIds?.length
  })
  const { handleApi: updateEvents } = useApi({ method: 'PUT', url: 'schedule/partial' })
  const { handleApi: sendConfirmationEmails } = useApi({ method: 'POST', url: 'schedule/confirm' })

  const hasPublished = selectedEvents?.some(event => event.published) ?? false
  useEffect(() => {
    const fetchOldEvent = async () => {
      const { response, isError } = await fetchselectedEvents<MData.Event[]>({ shiftIds, showCancelledShift: true })
      if (isError || !response?.length) return setErrorLoading(true)
    }
    if (shiftIds && shiftIds.length) fetchOldEvent()
  }, [fetchselectedEvents, shiftIds])

  // Function that sets the initial value of the employeeId field
  const getInitialFieldValue = (events: MData.Event[]): string => {
    const allSame = events.every(event => event.employeeId === events[0].employeeId)
    return allSame ? events[0].employeeId : 'multipleValues'
  }

  const parseEvents = (events: MData.Event[]): MData.Event[] => {
    return events.map((event: MData.Event) => ({
      ...event,
      startDate: Format(event.start, { outFormat: 'date' }),
      start: Format(event.start, { outFormat: 'serverTime' }),
      end: Format(event.end, { outFormat: 'serverTime' })
    }))
  }

  const onSubmit = async ({ sendCancellationEmails, sendNotificationForNewEmployee, updateFutureRecurrence, employeeId }: ValuesType) => {
    const updatedEvents = selectedEvents?.map(event => {
      return { ...event, employeeId }
    }) as MData.Event[]
    await updateEvents({
      events: updatedEvents,
      options: {
        sendCancellationEmails,
        updateFutureRecurrence
      }
    })
    // Send a notification to the newly assigned employee
    if (sendNotificationForNewEmployee) {
      const newEventsForEmployee = selectedEvents?.filter(event => event.employeeId !== updatedEvents[0].employeeId)
      const body = {
        shiftIds: newEventsForEmployee?.map(event => event._id),
        options: { confirm: true, reconfirm: true, type: 'confirmation' }
      }
      sendConfirmationEmails(body)
    }
    // Track that user has edited a shift
    TrackAction('EDIT_SHIFT')
    dispatch(closeModal())
  }

  const shiftShape = {
    employeeId: Yup.string().nullable(),
    updateFutureRecurrence: Yup.boolean().nullable(),
    sendCancellationEmails: Yup.boolean(),
    sendNotificationForNewEmployee: Yup.boolean()
  }

  const UserAssignForm = () => (
    <Formik
      enableReinitialize={true}
      validationSchema={Yup.object().shape(shiftShape)}
      initialValues={{
        employeeId: getInitialFieldValue(selectedEvents as MData.Event[]),
        updateFutureRecurrence: false,
        sendCancellationEmails: hasPublished,
        sendNotificationForNewEmployee: hasPublished
      }}
      onSubmit={values => onSubmit(values)}
    >
      {({ values, status, isSubmitting, setStatus, dirty, setFieldValue }) => {
        const displayOptions = dirty && hasPublished
        const displayAlert = dirty && (values.sendNotificationForNewEmployee || values.sendCancellationEmails)
        // Props for the multiple values component
        const mvProps = { values, setFieldValue }
        const hasRecurrence = selectedEvents?.length === 1 && selectedEvents[0].rruleId
        const inContactList = selectedEvents?.some(event => event.contactListId)
        return (
          <Form>
            {/* Error message */}
            <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
              {status?.msg}
            </Alert>
            <div className="mb-2">
              <EventSummaryList events={parseEvents(selectedEvents as MData.Event[])} isTime />
            </div>
            <MultipleValuesInput
              selectedEvents={selectedEvents ?? []}
              parseEvents={parseEvents}
              name="employeeId"
              {...mvProps}
              disabled={!hasAccessToThis('scheduleAssignEmployee')}
            >
              <UserInput required />
            </MultipleValuesInput>
            <Alert variant="warning" show={!!inContactList}>
              {t('manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL.SHIFT_IN_LIST_WARNING')}
            </Alert>

            {/* Options */}
            {displayOptions ? (
              <Card>
                <Card.Body className="bg-light mt-2">
                  {/* Whether or not to delete future recurrence */}
                  {hasRecurrence ? <Checkbox module="manager.schedule.eventForm" name="updateFutureRecurrence" /> : null}
                  {hasPublished ? (
                    <>
                      {/* Notification to send to the newly assigned employee */}
                      <Checkbox module="manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL" name="sendNotificationForNewEmployee" />
                      {/* Notification to send to the former employee*/}
                      <Checkbox module="manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL" name="sendCancellationEmails" />
                    </>
                  ) : null}
                </Card.Body>
              </Card>
            ) : null}
            <Alert variant="warning" className="my-2" noIcon show={!!displayAlert}>
              {values.sendNotificationForNewEmployee ? (
                <div>
                  <i className={`fas fa-exclamation-triangle mr-2`} />
                  {t('manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL.ALERT_CONFIRM_SHIFTS')}
                </div>
              ) : null}
              {values.sendCancellationEmails ? (
                <div>
                  <i className={`fas fa-exclamation-triangle mr-2`} />
                  {t('manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL.SEND_CANCELLATION_NOTICE')}
                </div>
              ) : null}
            </Alert>

            {/* Submit & Cancel buttons */}
            <div className="mt-2">
              <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" disabled={!dirty} />
              <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                {t(`actions.CLOSE`)}
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )

  if (isLoading) return <Loading />

  return (
    <BaseModal hideButtons noPadding title={`manager.schedule.SCHEDULE_ASSIGN_EMPLOYEE_MODAL`}>
      {errorLoading ? (
        <>
          <Alert variant="warning" className="mt-2">
            {t('manager.schedule.MULTI_EDIT_MODAL.ERROR_LOADING')}
          </Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary mt-2" onClick={() => dispatch(closeModal())} />
        </>
      ) : (
        <div className="pt-2 px-3 pb-3">{<UserAssignForm />}</div>
      )}
    </BaseModal>
  )
}

const MultipleValuesInput = ({
  children,
  className,
  name = '',
  values,
  setFieldValue,
  module = 'manager.schedule.eventForm',
  disabled,
  parseEvents,
  selectedEvents
}: MultipleValuesInputType) => {
  if (values[name] !== 'multipleValues') return <div className={`${className} p-2`}>{children}</div>

  return (
    <div className="form-group">
      <div className="d-flex flex-column">
        <label className="control-label">{t(`${module}.${name.toUpperCase()}.LABEL`)}</label>
      </div>
      <div className="input-group mb-3">
        <Input disabled type="text" placeholder={t('manager.schedule.eventForm.MULTIPLE_VALUES')} />
        <div className="input-group-append">
          <Button
            className="btn btn-outline-secondary"
            text="manager.schedule.eventForm.CLICK_TO_EDIT"
            onClick={() => {
              const valueToDisplay: string | null = parseEvents(selectedEvents as MData.Event[])[0][name] ?? null
              return setFieldValue(name, valueToDisplay)
            }}
            disabled={disabled}
          />
        </div>
      </div>

      <small className="form-text text-muted">{t(`${module}.${name.toUpperCase()}.DESCRIPTION`)}</small>
    </div>
  )
}

export default ScheduleAssignEmployeeModal
